/**
 * Contract acknowledgement copy, version identifier and forbidden-claim
 * lists (Task 303-B).
 *
 * Source of truth:
 * - `tasks/303B-client-contract-acknowledgement.md` §2.9, §2.10, §2.11
 * - `tasks/302-contract-acknowledgement-copy.md` (PR #355) — the copy
 *   proposal this file consumes, with ONE amended clause (below)
 * - `supabase/migrations/20260913120000_agency_client_contract_acknowledgement.sql`
 *
 * THIS IS NOT AN ELECTRONIC SIGNATURE, AND NOTHING HERE MAY SAY IT IS.
 * The acknowledgement is an append-only audit record ABOUT an externally
 * executed PDF (`docs/decisions/2026-08-24-commercial-launch-posture.md`
 * CLP-1.8; 303-A §2.4). The externally signed artifact is the contract;
 * this record is evidence that a named user reviewed a named version of
 * it on a named date.
 *
 * ONE CLAUSE DIFFERS FROM TASK 302, AND THE REASON IS RECORDED RATHER
 * THAN SMOOTHED OVER. Task 302 clause 3 asserts the reader saw the
 * document's «بصمته الرقمية» / checksum, and its §5 U3 makes displaying
 * the full SHA-256 a condition of the text being true. 303-A §2.10 and
 * 303-A-B §B5 both WITHHOLD `sha256` from the client projection, the
 * Product Owner reaffirmed that withheld list, and the 303-B scope
 * statement forbids exposing it independently. A statement asserting a
 * fact that is not on screen is false as rendered — Task 302 says so
 * itself. Clause 3 therefore names the three identifying facts the client
 * IS shown: version number, file name, availability date.
 *
 * Clauses 1, 2, 4, 5 and 6 are Task 302's, byte for byte. Clause 6 is the
 * one the truthful-negative allow-list carves out, so the carve-out still
 * pins the exact text it was written for.
 *
 * AND CLAUSE 3 CHANGES ONLY ITS NOUNS. An independent copy review caught an
 * earlier draft that also swapped the preposition — «مع اسمه…» became
 * «برقم نسخته…» — and that was the load-bearing edit, not the nouns.
 * «مع» asserts CO-PRESENCE on screen, which is the entire evidentiary
 * point of the clause; «بـ» with no governing participle is ambiguous
 * between an accompaniment adverbial and a tautological appositive ("the
 * document … bearing its own version number"), the second of which asserts
 * nothing about the screen at all. The same review found the English had
 * drifted to "identified by", a participle the Arabic had no counterpart
 * for — so the two locales were no longer the one statement in two
 * renderings that a single `ack_text_version` rests on. Both are restored
 * to Task 302's «مع» / "together with", and the comma an earlier draft
 * inserted between clauses 2 and 3 is gone: punctuation is
 * version-significant under Task 302 §4.3 rule 1.
 *
 * The digest is still bound into the RECORD, server-side, at action time
 * (`agency_client_contract_acknowledgements.document_sha256`). CLP-1.8
 * asks the stored row to identify which artifact was acknowledged; it does
 * not ask the screen to display a hash.
 */

/**
 * The version identifier written to
 * `agency_client_contract_acknowledgements.ack_text_version`.
 *
 * ONE identifier for BOTH locales. Task 302 §4.2's reason is mechanical,
 * not aesthetic: idempotency is keyed on this value, so per-locale
 * identifiers would let one user append a second, factless row simply by
 * re-opening the page in the other locale — the exact duplicate the rule
 * exists to prevent, arriving through the one door it does not watch. The
 * consequence, recorded: the record does not capture which locale was
 * read. That needs a column, not a version-string overload.
 *
 * It must equal `app.agency_client_contract_ack_text_version()` in the
 * migration. No single test can see both languages at once, so the
 * agreement is pinned from both sides — a Vitest on this constant and a
 * SQL pack assertion on that function — and this comment says so rather
 * than implying one test proves it.
 *
 * WHY `v2` WHEN NOTHING CARRIES `v1`.
 *
 * An earlier draft used `contract-ack-v1` on the reasoning that no row
 * anywhere carries it, so it is still a first version in the literal sense.
 * An independent copy review pointed out that the reasoning is aesthetic
 * and the risk it dismisses is mechanical: PR #355 proposes a DIFFERENT
 * statement under `contract-ack-v1`, nothing in CI fails if that PR merges,
 * and the repository would then hold two texts under one identifier —
 * precisely what the column exists to prevent. The mitigation was a
 * cross-PR coordination note; the fix is one character.
 *
 * It also stops being free the moment staging writes rows, which the
 * rollout plan does. So it is done now, before any row exists, and
 * `contract-ack-v1` is left to mean exactly what PR #355 says it means.
 */
export const CONTRACT_ACK_TEXT_VERSION = 'contract-ack-v2';

/**
 * The rendered statement. Arabic is the product's only rendering locale
 * (`apps/web/index.html` is `<html lang="ar" dir="rtl">`; there is no i18n
 * layer and no translation dependency).
 *
 * UNVOCALISED ON PURPOSE. The string is pinned byte-exactly by a copy test
 * and stored against a version identifier; a dropped shadda in a re-typed
 * string is a silent version mismatch. Context disambiguates every word
 * here.
 */
export const CONTRACT_ACK_STATEMENT_AR =
  'أقر بأنني اطلعت على مستند العقد المعروض في هذه الصفحة مع رقم نسخته واسم ملفه وتاريخ إتاحته. ' +
  'وتسجل المنصة هذا الإقرار باسمي وبتاريخه كسجل اطلاع على هذا المستند بعينه، ' +
  'ولا يقوم هذا الإقرار مقام التوقيع على العقد.';

/**
 * The semantically equivalent reference text.
 *
 * CHECKED IN AND PINNED, NOT RENDERED. Task 302 **I-1**: the product has
 * no English rendering path at all. This constant exists so the record's
 * meaning can be explained in English and so both locales are pinnable by
 * the same matcher — not because a screen displays it. Making it
 * user-visible is an i18n scope item, filed as a follow-up.
 */
export const CONTRACT_ACK_STATEMENT_EN =
  'I acknowledge that I have reviewed the contract document displayed on this page, ' +
  'together with its version number, its file name and the date it was made available. ' +
  'The platform records this acknowledgement in my name, with its date, ' +
  'as a record of my review of this specific document, ' +
  'and this acknowledgement does not take the place of signing the contract.';

export type ContractAckLocale = 'ar' | 'en';

/**
 * A forbidden CLAIM, not a forbidden word.
 *
 * `wordBoundary` exists for exactly one entry and is documented at its
 * site. Everything else is a plain substring match, per Task 302 §6.5
 * rule 4.
 */
export interface ForbiddenAckClaim {
  readonly group: ContractAckForbiddenGroup;
  readonly entry: string;
  readonly wordBoundary?: true;
}

export type ContractAckForbiddenGroup =
  | 'A_signature_status'
  | 'A_clicking_is_signing'
  | 'B_legal_effect'
  | 'C_scope_expansion'
  | 'D_identity_verification'
  | 'E_commercial_terms'
  | 'F_jurisdiction'
  | 'G_authority_to_bind'
  | 'H_finality';

/** The groups the truthful-negative allow-list may excuse. Only A. */
export const CONTRACT_ACK_EXCUSABLE_GROUPS: ReadonlySet<ContractAckForbiddenGroup> = new Set([
  'A_signature_status',
  'A_clicking_is_signing',
]);

/**
 * Task 302 §6.2, EXTENDED — not verbatim, and an independent review
 * found this docblock still claiming otherwise after the task file had
 * been corrected. The additions and their direction are itemised in
 * `tasks/303B-…md` §2.11; in short, Group A gained the
 * signature-equivalence family and its inflections, Group B the inserted
 * pronoun and the "legal force" forms, and Group H is bound to the
 * acknowledgement rather than to any action.
 *
 * Group A entries are SHORT ORDER-INDEPENDENT CORES, not sentences, and
 * that is the correction Task 302 records against its own first draft:
 * Arabic permits subject-first as readily as verb-first, so
 * `هذا الإقرار يقوم مقام التوقيع على العقد` makes the forbidden claim while
 * sharing no contiguous substring with a verb-first entry.
 *
 * Group D is an ENUMERATED list, never a heuristic: the bare token `نفاذ`
 * also means "effect" or "validity" in ordinary Arabic (`نفاذ المفعول`),
 * so a bare-token ban would fire on truthful copy. Adding another national
 * identity provider is a visible amendment here, not a widened pattern.
 *
 * Group H exists because a `revocation` record is part of CLP-1.8's model.
 * 303-B ships no revocation path, which makes the group MORE necessary,
 * not less: copy claiming irrevocability would assert a legal property the
 * product has not decided, and copy promising withdrawal would be false.
 *
 * † `مقام التوقيع` collides with the approved statement's own limiting
 * clause and is excused ONLY by exact clause equality against
 * `CONTRACT_ACK_TRUTHFUL_NEGATIVES`.
 */
export const CONTRACT_ACK_FORBIDDEN_AR: readonly ForbiddenAckClaim[] = [
  { group: 'A_signature_status', entry: 'توقيع إلكتروني' },
  { group: 'A_signature_status', entry: 'التوقيع الإلكتروني' },
  { group: 'A_signature_status', entry: 'توقيع رقمي' },
  { group: 'A_signature_status', entry: 'توقيع مصدق' },
  { group: 'A_signature_status', entry: 'توقيع معتمد' },
  { group: 'A_signature_status', entry: 'توقيعي' },
  { group: 'A_signature_status', entry: 'توقيعه' },
  { group: 'A_signature_status', entry: 'تصديق إلكتروني' },
  { group: 'A_signature_status', entry: 'توقيع اليد' },
  { group: 'A_signature_status', entry: 'التوقيع اليدوي' },
  { group: 'A_signature_status', entry: 'شهادة توقيع' },
  { group: 'A_signature_status', entry: 'توقيعك' },
  { group: 'A_signature_status', entry: 'توقيعا' },

  // AN INDEPENDENT COPY REVIEW FOUND THIS FAMILY MISSING, and it was the
  // sharpest finding against this file. Task 302 §6.2 claims that banning
  // the order-independent core "closes the family"; it closed one idiom.
  // Every one of these passed the shipped matcher:
  //
  //   «هذا الإقرار يحل محل التوقيع على العقد»   — the EXACT Arabic of
  //     `takes the place of signing`, which IS listed in English. The two
  //     locales were asymmetric on the single most important claim.
  //   «يعتبر هذا الإقرار بمثابة التوقيع»
  //   «هذا الإقرار في حكم التوقيع»
  //   «هذا هو التوقيع الرقمي المعتمد»            — the definite form, while
  //     the definite `التوقيع الإلكتروني` was listed. An omission inside
  //     the list's own pattern.
  //   «هذا الإقرار يعد إمضاء إلكترونيا»          — `إمضاء` is the other
  //     Arabic word for signature and appeared nowhere.
  //
  // None of them collides with the approved statement, which says
  // `مقام التوقيع` and nothing else from this family.
  { group: 'A_signature_status', entry: 'التوقيع الرقمي' },
  { group: 'A_signature_status', entry: 'امضاء' },

  { group: 'A_clicking_is_signing', entry: 'مقام التوقيع' }, // †
  { group: 'A_clicking_is_signing', entry: 'يحل محل التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'محل التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'بمثابة التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'في حكم التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'حكم التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'تكون قد وقعت' },
  { group: 'A_clicking_is_signing', entry: 'وقعت على العقد' },
  { group: 'A_clicking_is_signing', entry: 'وقع إلكترونيا' },
  { group: 'A_clicking_is_signing', entry: 'اضغط للتوقيع' },
  { group: 'A_clicking_is_signing', entry: 'اضغط لتوقيع' },
  { group: 'A_clicking_is_signing', entry: 'وقعت العقد' },
  { group: 'A_clicking_is_signing', entry: 'توقيع العقد' },
  // Arabic permits arbitrary insertion between the verb and its
  // prepositional object, so an enumerated list is a FLOOR here, never a
  // ceiling: «يغني هذا الإقرار عن التوقيع» shares no contiguous substring
  // with «يغني عن التوقيع». The common subject insertions are enumerated
  // rather than replaced by a proximity heuristic, which Task 302 §6.4
  // rejects for good reasons.
  { group: 'A_clicking_is_signing', entry: 'يغني عن التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'يغني هذا الإقرار عن التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'يغني الإقرار عن التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'يساوي التوقيع' },
  { group: 'A_clicking_is_signing', entry: 'قائم مقام التوقيع' },

  // Bound to the ADJECTIVE plus the adverb, with the common inserted
  // pronoun covered separately: «ملزم لك قانونا» defeated `ملزم قانونا`
  // with one word.
  { group: 'B_legal_effect', entry: 'ملزم قانونا' },
  { group: 'B_legal_effect', entry: 'ملزم قانونيا' },
  { group: 'B_legal_effect', entry: 'ملزم لك قانونا' },
  { group: 'B_legal_effect', entry: 'ملزم لكم قانونا' },
  { group: 'B_legal_effect', entry: 'نافذ قانونا' },
  { group: 'B_legal_effect', entry: 'قوة قانونية' },
  { group: 'B_legal_effect', entry: 'حجة عليك' },
  { group: 'B_legal_effect', entry: 'أمام القضاء' },
  { group: 'B_legal_effect', entry: 'له نفس الأثر القانوني' },
  { group: 'B_legal_effect', entry: 'يعادل التوقيع' },
  { group: 'B_legal_effect', entry: 'حجة قانونية' },
  { group: 'B_legal_effect', entry: 'سند تنفيذي' },

  { group: 'C_scope_expansion', entry: 'أوافق على جميع الشروط' },
  { group: 'C_scope_expansion', entry: 'أوافق على كل الشروط' },
  { group: 'C_scope_expansion', entry: 'أقبل جميع الشروط' },
  { group: 'C_scope_expansion', entry: 'أقبل كل الشروط' },
  { group: 'C_scope_expansion', entry: 'بما في ذلك أي شروط أخرى' },
  { group: 'C_scope_expansion', entry: 'الشروط المشار إليها' },
  { group: 'C_scope_expansion', entry: 'أي تعديلات لاحقة' },
  { group: 'C_scope_expansion', entry: 'الشروط والأحكام المرفقة' },

  { group: 'D_identity_verification', entry: 'التحقق من هويتي' },
  { group: 'D_identity_verification', entry: 'التحقق من هويتك' },
  { group: 'D_identity_verification', entry: 'أبشر' },
  { group: 'D_identity_verification', entry: 'نفاذ الوطني' },
  { group: 'D_identity_verification', entry: 'هوية موثقة' },
  { group: 'D_identity_verification', entry: 'توثيق الهوية' },
  { group: 'D_identity_verification', entry: 'بوابة نفاذ' },
  { group: 'D_identity_verification', entry: 'عبر نفاذ' },

  { group: 'E_commercial_terms', entry: 'أتعهد بالسداد' },
  { group: 'E_commercial_terms', entry: 'أوافق على الدفع' },
  { group: 'E_commercial_terms', entry: 'تستحق الفاتورة' },
  { group: 'E_commercial_terms', entry: 'رسوم غير مستردة' },
  { group: 'E_commercial_terms', entry: 'ضريبة القيمة المضافة' },

  { group: 'F_jurisdiction', entry: 'القانون الواجب التطبيق' },
  { group: 'F_jurisdiction', entry: 'المحاكم المختصة' },
  { group: 'F_jurisdiction', entry: 'تخضع لأنظمة' },
  { group: 'F_jurisdiction', entry: 'يخضع لأنظمة' },
  { group: 'F_jurisdiction', entry: 'أنظمة المملكة' },
  { group: 'F_jurisdiction', entry: 'اللجوء إلى التحكيم' },
  { group: 'F_jurisdiction', entry: 'ديوان المظالم' },

  { group: 'G_authority_to_bind', entry: 'بصفتي مفوضا' },
  { group: 'G_authority_to_bind', entry: 'مفوض بالتوقيع' },
  { group: 'G_authority_to_bind', entry: 'نيابة عن الشركة' },
  { group: 'G_authority_to_bind', entry: 'ممثلا قانونيا' },

  // THE SAME REVIEW FOUND GROUP H NEARLY DECORATIVE. «لا يمكن التراجع عن
  // هذا الإقرار», «لا يمكن سحب هذا الإقرار» and «هذا الإقرار نهائي ودائم»
  // all passed. 303-B ships no revocation path, which is exactly why this
  // group matters MORE here than Task 302 needed it to: a blanket "you can
  // never take this back" is a claim about a legal effect the product has
  // not decided, on a record whose correction model (CLP-1.8) is filed and
  // unbuilt.
  //
  // The honest product fact — that the platform offers no undo and a
  // correction goes through the agency — is a DIFFERENT statement and is
  // deliberately still sayable: it asserts what the software does, not what
  // the record legally is. The UI says it in those terms.
  { group: 'H_finality', entry: 'غير قابل للإلغاء' },
  { group: 'H_finality', entry: 'لا رجعة فيه' },
  { group: 'H_finality', entry: 'غير قابل للسحب' },
  // BARE CORES, RESTORED — and the round that narrowed them is the reason
  // they are back with a note.
  //
  // A previous round replaced `لا يمكن التراجع` / `لا يمكن سحب` /
  // `لا يمكن إلغاء` with subject-bound sentences, to stop them firing on
  // «لا يمكن إلغاء التنزيل بعد بدئه». Arabic fronts subjects freely, so
  // only ONE word order survived: «هذا الإقرار لا يمكن التراجع عنه»,
  // «لا يمكن سحب الإقرار», «إقرارك لا يمكن التراجع عنه» and three more
  // went from caught to clean. A mutation-tested review found all six.
  //
  // The premise was the mistake. Task 302 §6.1 scopes these lists to
  // ACKNOWLEDGEMENT COPY — the block, the page's acknowledgement handler,
  // and the endpoint's user-facing strings. Download copy is not in that
  // slice and never was, so the "false positive" was an invented fixture
  // rather than a rule firing on shipped text. The cores are broad on
  // purpose: inside this slice there is no truthful reason to say the
  // acknowledgement cannot be taken back.
  { group: 'H_finality', entry: 'لا يمكن التراجع' },
  { group: 'H_finality', entry: 'لا يمكنك التراجع' },
  { group: 'H_finality', entry: 'لا يمكن سحب' },
  { group: 'H_finality', entry: 'لا يمكنك سحب' },
  { group: 'H_finality', entry: 'لا يمكن إلغاء' },
  { group: 'H_finality', entry: 'لا يمكنك إلغاء' },
  { group: 'H_finality', entry: 'لا يمكن إلغاؤه' },
  { group: 'H_finality', entry: 'لا يمكن سحبه' },
  { group: 'H_finality', entry: 'غير قابل للتراجع' },
  { group: 'H_finality', entry: 'لا يجوز الرجوع' },
  { group: 'H_finality', entry: 'يستحيل حذفه' },
  { group: 'H_finality', entry: 'نهائي ودائم' },
  { group: 'H_finality', entry: 'دائم ونهائي' },
  { group: 'H_finality', entry: 'نهائي وغير' },
];

/**
 * Task 302 §6.3, EXTENDED, with one NARROWING. Not verbatim: `VAT` is
 * word-bounded (the narrowing, and the reason is at its site), and Groups
 * A and H gained the mirrors of the Arabic additions above. Itemised in
 * `tasks/303B-…md` §2.11.
 *
 * `VAT` is matched on WORD BOUNDARIES rather than as a bare substring.
 * Task 302 lists it as a plain entry, and under §6.5 rule 4 (lowercase,
 * then substring) a three-letter token is a false-positive generator:
 * `activation` contains `vat`, and this product's own vocabulary is full
 * of activation. A rule that fires on truthful copy gets deleted by the
 * next editor rather than fixed, which costs the whole group.
 *
 * This is not a loosening, and it is not a new idea — it is the SAME
 * remedy Task 302 §6.2 applied to Group D, where it refused to ban the
 * bare token `نفاذ` because it also means "validity" in ordinary Arabic.
 * A bounded `vat` still catches `VAT`, `VAT.` and `(VAT)`, and stops
 * catching `activation`.
 *
 * Both `take the place of signing` and `takes the place of signing` are
 * listed on purpose. Only the first collides with the approved statement;
 * the second is the affirmative form the list actually exists to catch,
 * and omitting it would leave the ban decorative.
 */
export const CONTRACT_ACK_FORBIDDEN_EN: readonly ForbiddenAckClaim[] = [
  { group: 'A_signature_status', entry: 'qualified electronic signature' },
  { group: 'A_signature_status', entry: 'certified electronic signature' },
  { group: 'A_signature_status', entry: 'advanced electronic signature' },
  { group: 'A_signature_status', entry: 'digital signature certificate' },
  { group: 'A_signature_status', entry: 'legally binding signature' },
  { group: 'A_signature_status', entry: 'your electronic signature' },
  { group: 'A_signature_status', entry: 'e-signature' },
  { group: 'A_signature_status', entry: 'esignature' },

  { group: 'A_clicking_is_signing', entry: 'constitutes your signature' },
  { group: 'A_clicking_is_signing', entry: 'constitutes a signature' },
  { group: 'A_clicking_is_signing', entry: 'is a signature' },
  { group: 'A_clicking_is_signing', entry: 'by clicking you are signing' },
  { group: 'A_clicking_is_signing', entry: 'you are signing this contract' },
  { group: 'A_clicking_is_signing', entry: 'take the place of signing' }, // †
  { group: 'A_clicking_is_signing', entry: 'takes the place of signing' }, // †
  { group: 'A_clicking_is_signing', entry: 'click to sign' },
  { group: 'A_clicking_is_signing', entry: 'sign electronically' },
  // Mirrors of the Arabic additions, so the two locales stay symmetric on
  // the one claim that matters most.
  { group: 'A_clicking_is_signing', entry: 'in lieu of signing' },
  { group: 'A_clicking_is_signing', entry: 'stands in for a signature' },
  { group: 'A_clicking_is_signing', entry: 'serves as a signature' },
  { group: 'A_clicking_is_signing', entry: 'serves as your signature' },
  { group: 'A_clicking_is_signing', entry: 'counts as a signature' },
  { group: 'A_clicking_is_signing', entry: 'has the force of a signature' },
  { group: 'A_clicking_is_signing', entry: 'acts as a signature' },
  { group: 'A_clicking_is_signing', entry: 'the equivalent of a signature' },
  { group: 'A_clicking_is_signing', entry: 'you have signed' },
  { group: 'A_clicking_is_signing', entry: 'electronically sign' },
  // WORD-BOUNDED, for the same reason `vat` is. `esign` as a bare
  // substring matches `design`, `redesign` and `designated`, in a
  // repository whose own vocabulary is `designer` and
  // `task.design.execute` — verified against the shipped matcher, where
  // "Our design team will contact you." came out dirty. A rule that fires
  // on truthful copy gets deleted rather than fixed.
  { group: 'A_clicking_is_signing', entry: 'esign', wordBoundary: true },
  { group: 'A_signature_status', entry: 'digital signature' },
  { group: 'A_signature_status', entry: 'e signature', wordBoundary: true },
  { group: 'A_signature_status', entry: 'wet signature' },
  { group: 'A_signature_status', entry: 'handwritten signature' },

  { group: 'B_legal_effect', entry: 'legally binding' },
  { group: 'B_legal_effect', entry: 'same legal effect' },
  { group: 'B_legal_effect', entry: 'equivalent to a handwritten signature' },
  { group: 'B_legal_effect', entry: 'enforceable instrument' },

  { group: 'C_scope_expansion', entry: 'agree to all terms' },
  { group: 'C_scope_expansion', entry: 'including any other terms' },
  { group: 'C_scope_expansion', entry: 'terms referenced herein' },
  { group: 'C_scope_expansion', entry: 'any future amendments' },
  { group: 'C_scope_expansion', entry: 'attached terms and conditions' },

  { group: 'D_identity_verification', entry: 'identity verified' },
  { group: 'D_identity_verification', entry: 'verified identity' },
  { group: 'D_identity_verification', entry: 'government-verified' },
  { group: 'D_identity_verification', entry: 'nafath' },

  { group: 'E_commercial_terms', entry: 'agree to pay' },
  { group: 'E_commercial_terms', entry: 'payment is due' },
  { group: 'E_commercial_terms', entry: 'invoice is due' },
  { group: 'E_commercial_terms', entry: 'non-refundable' },
  { group: 'E_commercial_terms', entry: 'vat', wordBoundary: true },

  { group: 'F_jurisdiction', entry: 'governing law' },
  { group: 'F_jurisdiction', entry: 'courts of competent jurisdiction' },
  { group: 'F_jurisdiction', entry: 'subject to the laws of' },
  { group: 'F_jurisdiction', entry: 'submit to arbitration' },

  { group: 'G_authority_to_bind', entry: 'authorized representative' },
  { group: 'G_authority_to_bind', entry: 'on behalf of the company' },
  { group: 'G_authority_to_bind', entry: 'duly authorized to sign' },

  { group: 'H_finality', entry: 'irrevocable' },
  { group: 'H_finality', entry: 'cannot be revoked' },
  { group: 'H_finality', entry: 'cannot be withdrawn' },
  { group: 'H_finality', entry: 'cannot be undone' },
  { group: 'H_finality', entry: 'cannot be reversed' },
  { group: 'H_finality', entry: 'irreversible' },
  { group: 'H_finality', entry: 'no way to undo' },
  { group: 'H_finality', entry: 'permanent record' },
  { group: 'H_finality', entry: 'permanent and final' },
  { group: 'H_finality', entry: 'final and permanent' },
  { group: 'H_finality', entry: 'final and non-appealable' },
];

/**
 * Task 302 §6.4 — the exact truthful-negative allow-list.
 *
 * A forbidden match FAILS BY DEFAULT. It is excused only when the ENTIRE
 * normalized clause is equal to an entry here. Not "contains", not
 * "starts with", not "a negation appears nearby":
 * `لا تتردد، هذا الإقرار يقوم مقام التوقيع` has a negation in it and is
 * still a forbidden claim.
 *
 * Two entries, both taken verbatim from the approved statement's clause 6
 * — the one clause the 303-B amendment does not touch. Widening this list
 * is a copy change requiring the same approval as editing the statement.
 *
 * It excuses GROUP A ONLY. There is no truthful-negative reason for
 * acknowledgement copy to contain a jurisdiction clause or a payment term
 * at all, so Groups B–H have no escape hatch and must not be given one.
 */
export const CONTRACT_ACK_TRUTHFUL_NEGATIVES: readonly string[] = [
  'ولا يقوم هذا الإقرار مقام التوقيع على العقد',
  'and this acknowledgement does not take the place of signing the contract',
];

/**
 * Upper bounds on the carve-out, so a future author cannot extend a clause
 * or add another to make new forbidden wording pass.
 *
 * This is 303-A §7.10 U4's finding applied here: a deny-list mechanism
 * with no upper bound is a deny-list mechanism that grows an exception
 * every time it is inconvenient. Both the count and the total excused
 * character budget are pinned, and a test asserts them.
 */
export const CONTRACT_ACK_TRUTHFUL_NEGATIVE_MAX_ENTRIES = 2;
export const CONTRACT_ACK_TRUTHFUL_NEGATIVE_MAX_CHARS = 160;

// Written as explicit escapes, never as the characters themselves. A
// character class of invisible or combining bytes cannot be reviewed in a
// diff, and one deleted by an editor would silently stop normalizing —
// which on a forbidden-claim matcher means a banned phrase starts passing.
//
// The range is WIDER than Task 302 §6.5 rule 3, which names
// U+064B-U+0652. It also strips U+0653-U+0655 and the superscript alef
// U+0670, because leaving those in place would let a banned phrase evade
// the list by spelling.
//
// AN EARLIER VERSION OF THIS COMMENT ARGUED THAT WIDENING "can only make a
// forbidden entry MORE likely to match, never less, so it cannot open a
// hole". An independent copy review showed that argument is unsound: the
// same normalization is applied to the truthful-negative ALLOW-LIST, which
// is matched by EQUALITY, so more folding can produce more EXCUSALS as well
// as more matches. The outcome here is still safe — the allow-list holds
// two entries, both Group A, both spelled exactly as the shipped statement
// spells them — but the reason is that the list is tiny and bounded, not
// that widening is monotone. The bound is asserted by count and by total
// excused characters.
const ARABIC_DIACRITICS = /[\u064B-\u0655\u0670]/g;
const ARABIC_TATWEEL = /\u0640/g;
const ARABIC_INDIC_DIGITS = /[\u0660-\u0669\u06F0-\u06F9]/g;

/**
 * Task 302 §6.5 rule 3, applied identically to the clause and to the list
 * entry, with one strengthening recorded at its site.
 *
 * Task 302 names the diacritic range `U+064B`–`U+0652`. This implementation
 * strips `U+064B`–`U+0655` and `U+0670` as well, because leaving superscript
 * alef in place would have let a forbidden phrase evade the list by
 * spelling.
 *
 * WIDENING IS NOT MONOTONE, and this docblock used to say it was. The
 * argument that more folding "can only make a forbidden entry MORE likely to
 * match" is unsound, for the reason recorded at ARABIC_DIACRITICS: the same
 * normalization is applied to the truthful-negative ALLOW-LIST, which is
 * matched by EQUALITY, so more folding can produce more EXCUSALS too. What
 * makes this particular widening safe is that the allow-list is tiny and
 * bounded, and the bound is asserted by count and by total excused
 * characters. Repeating the retracted claim here, four lines under its
 * retraction, is how a review found this.
 */
export function normalizeAckCopy(input: string, locale: ContractAckLocale): string {
  let text = input.normalize('NFC');
  text = text.replace(ARABIC_DIACRITICS, '');
  text = text.replace(ARABIC_TATWEEL, '');
  text = text.replace(ARABIC_INDIC_DIGITS, (ch) => {
    const code = ch.codePointAt(0) ?? 0;
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
  // alef with hamza above / below, alef madda, alef wasla -> bare alef;
  // alef maqsura -> yeh. Task 302 §6.5 rule 3.
  text = text.replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627');
  text = text.replace(/\u0649/g, '\u064A');
  // Bidi controls and zero-width joiners are INVISIBLE and would otherwise
  // let a banned phrase evade a substring match while rendering
  // identically — `ملزم\u200F قانونا` looks exactly like the phrase it
  // is spelled to dodge. Written as explicit escapes rather than as the
  // characters themselves, because a regex full of invisible bytes is
  // unreviewable and one deleted by an editor would silently reopen the
  // hole. The page's own LTR isolates live in JSX expressions, not in
  // copy, so nothing legitimate is lost.
  text = text.replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '');
  if (locale === 'en') text = text.toLowerCase();
  return text.replace(/\s+/g, ' ').trim();
}

/** Task 302 §6.5 rule 2 — split on `.` `،` `؛` `,` `;` and newline. */
export function splitAckClauses(input: string): string[] {
  return input
    .split(/[.،؛,;\n\r]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Task 302 §6.5 rule 1 — build the slice from source text.
 *
 * Drops block and line comments, `class` / `className` attribute values,
 * every remaining tag, and decodes the handful of entities that appear in
 * this codebase. Deliberately conservative: anything it fails to strip
 * stays IN the slice and can only cause a false positive, never a false
 * negative.
 */
export function buildAckCopySlice(source: string): string {
  let text = source;
  text = text.replace(/\/\*[\s\S]*?\*\//g, ' ');
  // Line comments only where a line comment can actually be: at the start
  // of a line or after whitespace. An independent review found the previous
  // `(^|[^:])//…` deleted to end-of-line from inside an attribute value —
  // `<a href="//cdn.example.com/x">…</a>` took the copy with it and the
  // slice came out clean, which is a FALSE NEGATIVE in a function whose
  // docblock promises it can only ever produce false positives.
  text = text.replace(/(^|[\s(;{,])\/\/[^\n]*/g, '$1 ');
  text = text.replace(/\{\s*\/\*[\s\S]*?\*\/\s*\}/g, ' ');
  text = text.replace(/(?:className|class)\s*=\s*(?:"[^"]*"|'[^']*'|\{[^}]*\})/g, ' ');
  // USER-VISIBLE ATTRIBUTE TEXT IS LIFTED OUT BEFORE THE TAGS GO.
  //
  // An independent copy review found that stripping `<[^>]*>` deleted every
  // attribute, so `<button aria-label="بالضغط تكون قد وقعت على العقد">أقر</button>`
  // reduced to « أقر » and came out CLEAN — while the identical claim as
  // plain text was caught. `aria-label` is a rendering surface: a screen
  // reader reads it aloud instead of the button's text. This page already
  // interpolates Arabic into `aria-label` elsewhere, so the hole was live.
  //
  // The values are COLLECTED and appended AFTER the tags are stripped, not
  // substituted in place. Substituting in place left the lifted text still
  // sitting between the angle brackets, so `<[^>]*>` swallowed it again on
  // the very next line and the fix did nothing — caught by the test written
  // for it, which is the point of writing the test for it.
  //
  // They are joined with a clause separator so two innocent attributes
  // cannot be concatenated into a claim neither of them makes.
  const attributeText: string[] = [];
  text = text.replace(
    // All five JSX spellings, not three. An independent review found
    // `aria-label={'…'}` and `aria-label={"…"}` — the idiomatic
    // string-in-braces forms — unlifted and then deleted with the tag.
    /\b(?:aria-label|aria-description|aria-roledescription|alt|title|placeholder|aria-placeholder|aria-valuetext|label|value|content)\s*=\s*(?:"([^"]*)"|'([^']*)'|\{\s*`([^`]*)`\s*\}|\{\s*'([^']*)'\s*\}|\{\s*"([^"]*)"\s*\})/g,
    (
      _m,
      dq: string | undefined,
      sq: string | undefined,
      tpl: string | undefined,
      bsq: string | undefined,
      bdq: string | undefined,
    ) => {
      attributeText.push(dq ?? sq ?? tpl ?? bsq ?? bdq ?? '');
      return ' ';
    },
  );
  text = text.replace(/<[^>]*>/g, ' ');
  if (attributeText.length > 0) text = `${text} . ${attributeText.join(' . ')}`;
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  return text.replace(/\s+/g, ' ');
}

export interface ForbiddenAckMatch {
  readonly group: ContractAckForbiddenGroup;
  readonly entry: string;
  readonly clause: string;
}

function entryMatches(
  clause: string,
  claim: ForbiddenAckClaim,
  locale: ContractAckLocale,
): boolean {
  const needle = normalizeAckCopy(claim.entry, locale);
  if (needle.length === 0) return false;
  if (claim.wordBoundary !== true) return clause.includes(needle);
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(clause);
}

/**
 * Task 302 §6.5 rules 3–4, with the §6.4 carve-out.
 *
 * Returns EVERY match rather than the first, so a fixture that is supposed
 * to trip three groups cannot pass by tripping one — and so a failure
 * message names what was found instead of only that something was.
 */
export function findForbiddenAckClaims(
  input: string,
  locale: ContractAckLocale,
): ForbiddenAckMatch[] {
  const list = locale === 'ar' ? CONTRACT_ACK_FORBIDDEN_AR : CONTRACT_ACK_FORBIDDEN_EN;
  // THE EXCUSED SET IS PER-LOCALE. An independent copy review found it was
  // built from BOTH entries and normalized with whichever locale was being
  // scanned, so the English entry entered the Arabic excused set
  // un-lowercased. Harmless while no entry is cross-locale — and not what
  // Task 302 §6.4 says, which is reason enough.
  const excused = new Set(
    CONTRACT_ACK_TRUTHFUL_NEGATIVES.filter((n) =>
      locale === 'ar' ? /[\u0600-\u06FF]/.test(n) : !/[\u0600-\u06FF]/.test(n),
    ).map((n) => normalizeAckCopy(n, locale)),
  );
  const matches: ForbiddenAckMatch[] = [];
  const seen = new Set<string>();

  const push = (claim: ForbiddenAckClaim, clause: string) => {
    const key = `${claim.group}|${claim.entry}|${clause}`;
    if (seen.has(key)) return;
    seen.add(key);
    matches.push({ group: claim.group, entry: claim.entry, clause });
  };

  for (const rawClause of splitAckClauses(input)) {
    const clause = normalizeAckCopy(rawClause, locale);
    if (clause.length === 0) continue;
    const clauseIsExcused = excused.has(clause);
    for (const claim of list) {
      if (!entryMatches(clause, claim, locale)) continue;
      // Exact clause equality only, and Group A only.
      if (clauseIsExcused && CONTRACT_ACK_EXCUSABLE_GROUPS.has(claim.group)) continue;
      push(claim, rawClause);
    }
  }

  // A SECOND PASS OVER THE WHOLE TEXT, for the groups that have no escape
  // hatch. An independent copy review found that Task 302 §6.5 rule 2's
  // clause split lets any banned phrase interrupted by punctuation walk
  // through: «هذا الإقرار ملزم، قانونا.» and `This is legally, binding.`
  // both split the phrase across two clauses and both came out clean.
  //
  // EVERY group is scanned, Group A included. An earlier version excluded
  // it on the reasoning that its entries are what the allow-list carves out
  // and a whole-text pass has no clause to compare for equality — which
  // left one comma defeating every signature-equivalence entry. Dropping
  // the excused clauses BEFORE the join is what makes the full scan safe.
  // A SECOND PASS OVER THE WHOLE TEXT, AND OVER EVERY GROUP.
  //
  // Task 302 §6.5 rule 2's clause split lets any banned phrase interrupted
  // by punctuation match neither half. An earlier version of this pass
  // covered groups B-H only, on the reasoning that Group A's entries are
  // what the allow-list carves out by exact clause equality, so a
  // whole-text scan "would refuse the approved statement itself".
  //
  // AN INDEPENDENT REVIEW DEMOLISHED THAT, empirically: it left one comma
  // walking every signature-equivalence claim past the matcher, in both
  // locales — «هذا الإقرار يقوم مقام، التوقيع على العقد.» and
  // `This is a, signature.` both came out clean, while
  // «هذا الإقرار ملزم، قانونا.» was correctly caught. The hole was open on
  // the one family this module exists for.
  //
  // The fix is to drop the EXCUSED CLAUSES before joining, rather than to
  // exclude a whole group after. What is left is by definition text the
  // allow-list does not cover, so every group can be scanned across it and
  // the approved statements still come out clean — clause 6 is removed
  // before the join, because it is byte-equal to an allow-list entry.
  const remainder = splitAckClauses(input)
    .filter((clause) => !excused.has(normalizeAckCopy(clause, locale)))
    .join(' ');
  const whole = normalizeAckCopy(remainder, locale);
  if (whole.length > 0) {
    for (const claim of list) {
      if (entryMatches(whole, claim, locale)) push(claim, whole);
    }
  }

  return matches;
}

/** Convenience for a test that only needs the verdict. */
export function ackCopyIsClean(input: string, locale: ContractAckLocale): boolean {
  return findForbiddenAckClaims(input, locale).length === 0;
}

/**
 * The wire shape of a receipt as it reaches a CLIENT.
 *
 * Two fields, both facts the client itself created: when it acknowledged,
 * and which statement version it confirmed. No digest, no byte size, no
 * storage key, no bucket, no tenant id, no client id, no user id and no
 * name — not even its own. Another user's receipt is not representable
 * here at all, because the SQL that builds it is pinned to `auth.uid()`.
 */
export interface ClientContractAcknowledgement {
  readonly acknowledged_at: string;
  readonly ack_text_version: string;
}

// =====================================================================
// The canonical identity rule — ONE definition, shared with the database
// =====================================================================

/**
 * The Unicode release both halves of the identity rule are transcribed from.
 *
 * Pinned as data, not prose, so the shape test can assert the migration cites
 * the same one. Updating Unicode is then a deliberate edit in three places
 * that fail together rather than a silent divergence.
 */
export const ACK_IDENTITY_UNICODE_VERSION = '17.0.0';

/** The normative files the ranges below are transcribed from. */
export const ACK_IDENTITY_UNICODE_SOURCES: readonly string[] = [
  'https://www.unicode.org/Public/17.0.0/ucd/PropList.txt',
  'https://www.unicode.org/Public/17.0.0/ucd/DerivedCoreProperties.txt',
];

/**
 * The invisible code-point RANGES that are PROVEN SAFE to trim off the ends
 * of a display identity, as `[first, last]` pairs.
 *
 * Unicode `White_Space`, plus exactly three others. Every one of them renders
 * nothing inline, reorders nothing, and has nothing to join at a string
 * boundary: the soft hyphen and the zero-width space are break opportunities,
 * and `U+FEFF` is the encoding artefact `String.prototype.trim()` already
 * removes — keeping it here is what makes JavaScript and SQL agree.
 *
 * EVERYTHING ELSE invisible is blank-only. That includes LRM, RLM and the
 * ARABIC LETTER MARK, which are not decoration: they change the resolved bidi
 * order of the run they sit in, so a trailing RLM after a Latin surname
 * inside an Arabic name is doing work.
 *
 * The two lists are not symmetric in risk, and that is the design. Leaving a
 * character out of this one keeps a cosmetic artefact on a stored name;
 * leaving one out of the blank list lets a name that renders as nothing pass
 * as an identity.
 */
export const ACK_IDENTITY_TRIM_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0009, 0x000d],
  [0x0020, 0x0020],
  [0x0085, 0x0085],
  [0x00a0, 0x00a0],
  [0x00ad, 0x00ad],
  [0x1680, 0x1680],
  [0x2000, 0x200b],
  [0x2028, 0x2029],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
  [0xfeff, 0xfeff],
];

/**
 * Every INVISIBLE code-point range. Authoritative, exhaustive, and a strict
 * superset of the trimmable ones.
 *
 * TRANSCRIBED from `ACK_IDENTITY_UNICODE_SOURCES` — `White_Space` and
 * `Default_Ignorable_Code_Point` — rather than reasoned about family by
 * family, because four review rounds each found one more omission from a
 * hand-built list: `U+200B`, then `U+E0100`, then `U+180F`, then `U+2065` and
 * `U+FFF0..U+FFF8`. Plus two further classes:
 * `General_Category=Cc` that is not `White_Space` — a name made only of BEL,
 * ESC or DEL is not a usable identity, and `U+0000` is absent mechanically
 * because PostgreSQL `text` cannot hold it — and two invisible `Cf` families
 * Unicode 17.0 does not mark Default_Ignorable: the interlinear annotation
 * marks (`U+FFF9..U+FFFB`) and the Egyptian hieroglyph format controls
 * (`U+13430..U+13440`) — and the VISUALLY BLANK characters that are neither
 * format nor default-ignorable: `U+2800` BRAILLE PATTERN BLANK and the two
 * Egyptian hieroglyph blanks. That last class is the one judgement rather
 * than a property lookup, and the criterion is that the reference glyph is
 * empty BY DESIGN. An omission there is a usability gap, never a leak.
 *
 * Used ONLY to answer "is anything visible here at all" — never to decide
 * what is stored.
 */
export const ACK_IDENTITY_BLANK_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0001, 0x0020],
  [0x007f, 0x00a0],
  [0x00ad, 0x00ad],
  [0x034f, 0x034f],
  [0x061c, 0x061c],
  [0x115f, 0x1160],
  [0x1680, 0x1680],
  [0x17b4, 0x17b5],
  [0x180b, 0x180f],
  [0x2000, 0x200f],
  [0x2028, 0x202f],
  [0x205f, 0x206f],
  [0x2800, 0x2800],
  [0x3000, 0x3000],
  [0x3164, 0x3164],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
  [0xffa0, 0xffa0],
  [0xfff0, 0xfffb],
  [0x13430, 0x13442],
  [0x1bca0, 0x1bca3],
  [0x1d173, 0x1d17a],
  [0xe0000, 0xe0fff],
];

/**
 * `Default_Ignorable_Code_Point` alone, as transcribed. Exported so a test
 * can assert the blank list still contains all of it after any edit — the
 * property is the reason the blank list is trusted, and a range dropped from
 * one without the other is exactly the failure this task kept hitting.
 */
export const ACK_UNICODE_DEFAULT_IGNORABLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x00ad, 0x00ad],
  [0x034f, 0x034f],
  [0x061c, 0x061c],
  [0x115f, 0x1160],
  [0x17b4, 0x17b5],
  [0x180b, 0x180f],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x206f],
  [0x3164, 0x3164],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
  [0xffa0, 0xffa0],
  [0xfff0, 0xfff8],
  [0x1bca0, 0x1bca3],
  [0x1d173, 0x1d17a],
  [0xe0000, 0xe0fff],
];

/**
 * Unicode `Bidi_Control`. A stored acknowledgement name may not contain ANY
 * of these — not at the ends, not in the middle.
 *
 * Trimming them off the ends was not enough. An INTERIOR override survives a
 * trim, and `U+202E` reverses everything after it, so a name can render on
 * the agency's receipt list as a string the person never typed. A receipt is
 * evidence; evidence that renders differently than it stores is worse than
 * no name at all.
 *
 * REJECTED, not repaired: stripping would store a name the person did not
 * choose and would disagree with every other surface showing it. The chain
 * falls through to the profile email and the auth email instead.
 *
 * The other half is `app.agency_client_contract_bidi_control_class` in
 * migration `20260913120000`; the shape test compares them.
 */
export const ACK_BIDI_CONTROL_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x061c, 0x061c],
  [0x200e, 0x200f],
  [0x202a, 0x202e],
  [0x2066, 0x2069],
];

function hasBidiControl(value: string): boolean {
  for (const ch of value) {
    const cp = ch.codePointAt(0)!;
    for (const [lo, hi] of ACK_BIDI_CONTROL_RANGES) if (cp >= lo && cp <= hi) return true;
  }
  return false;
}

function inRanges(cp: number, ranges: ReadonlyArray<readonly [number, number]>): boolean {
  for (const [lo, hi] of ranges) if (cp >= lo && cp <= hi) return true;
  return false;
}

/**
 * The value a receipt may carry as the acknowledging person's name, or `null`
 * when the input names nobody.
 *
 * TRIMS the ends, never the interior. `String.prototype.trim` removes
 * whitespace and `U+FEFF` and stops there — `U+200B`, `U+200E` and the bidi
 * overrides all survive it, so a name made of them "is not blank" while
 * rendering as an empty cell. That is the defect this closes.
 *
 * Interior code points are preserved exactly, and so is anything at the ends
 * that belongs to the character beside it. Iterates by CODE POINT, so an
 * astral character is never half-eaten.
 */
export function canonicalAckIdentity(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  // Any bidi control, anywhere, and the value is refused. Checked FIRST
  // because it is a rejection rather than a trim.
  if (hasBidiControl(value)) return null;
  const points = Array.from(value);
  // Is there anything at all, wherever it sits in the string?
  if (points.every((c) => inRanges(c.codePointAt(0)!, ACK_IDENTITY_BLANK_RANGES))) return null;
  let start = 0;
  let end = points.length;
  while (start < end && inRanges(points[start]!.codePointAt(0)!, ACK_IDENTITY_TRIM_RANGES))
    start += 1;
  while (end > start && inRanges(points[end - 1]!.codePointAt(0)!, ACK_IDENTITY_TRIM_RANGES))
    end -= 1;
  const trimmed = points.slice(start, end).join('');
  return trimmed.length > 0 ? trimmed : null;
}

/** The agency-side receipt. Adds the acknowledging user's display name. */
export interface AgencyContractAcknowledgement {
  readonly acknowledged_at: string;
  readonly ack_text_version: string;
  readonly acknowledged_by_name: string | null;
}
